// src/components/resources/ResourceLayout.tsx

'use client'

import { motion, useReducedMotion } from 'motion/react'
import useScroll from '~/lib/use-scroll'
import { Breadcrumbs } from '~/components/resources/Breadcrumbs'

/**
 * Props for the ResourceLayout component.
 */
interface ResourceLayoutProps {
  /** The main title to display in the header. */
  title?: string
  
  /** Optional description text to display below the title. */
  description?: string
  
  /** The main content to render within the layout. */
  children: React.ReactNode
  
  /**
   * Controls whether the visible header (title and description) is displayed.
   * When false, an sr-only heading is rendered for accessibility unless
   * suppressFallbackHeading is true.
   * @default true
   */
  showHeader?: boolean
  
  /**
   * When true, the layout will skip rendering the sr-only heading fallback
   * that normally preserves semantic structure when the visible header is
   * hidden. Consumers can opt-in when they provide their own accessible
   * heading (e.g., interactive quizzes with bespoke hero layouts).
   * @default false
   */
  suppressFallbackHeading?: boolean
}

/**
 * ResourceLayout provides a consistent layout structure for resource pages.
 * 
 * Features:
 * - Fixed top navigation with breadcrumbs
 * - Scroll-aware background gradient
 * - Optional animated header with title and description
 * - Accessible heading structure with sr-only fallback
 * - Responsive spacing and typography
 * 
 * @example
 * ```tsx
 * <ResourceLayout title="My Resource" description="Learn about...">
 *   <ResourceContent />
 * </ResourceLayout>
 * ```
 */
export function ResourceLayout({
  title = '',
  description = '',
  children,
  showHeader = true,
  suppressFallbackHeading = false
}: ResourceLayoutProps): JSX.Element {
  const scrolled = useScroll(50)
  // Reduced-motion gate (`r3548131824`): gate the entrance animations via the
  // TRANSITION only — `initial`/`animate` stay constant, so the SSR-emitted
  // inline style (opacity:0 + translateY) is byte-identical for every route
  // that renders this shared layout (no prerendered-HTML/fingerprint churn).
  // For reduced-motion users the client snaps content to its final state in a
  // zero-duration step at hydration instead of playing the fade/slide.
  const prefersReducedMotion = useReducedMotion()
  const contentTransition = prefersReducedMotion
    ? { duration: 0, delay: 0 }
    : { delay: 0.2 }

  const normalizedTitle = title.trim()
  const normalizedDescription = description.trim()
  const shouldRenderVisibleHeader = showHeader && normalizedTitle.length > 0

  return (
    <div className="min-h-screen">
      {/* Fixed top navigation bar that extends full width */}
      <div className="fixed top-0 left-0 right-0 z-40">
        {/* Background that extends up */}
        <div className={`absolute inset-0 transition-opacity duration-200 ${
            scrolled
              ? 'bg-gradient-to-b from-white via-white to-white/95 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950/95'
            : 'bg-gradient-to-b from-white via-white/95 to-white/80 dark:from-gray-950 dark:via-gray-950/95 dark:to-gray-950/80'
        }`} />

        {/* Space for Navbar */}
        <div className="h-24" />
        
        {/* Breadcrumbs Container */}
        <div className="relative pb-1">
          <Breadcrumbs />
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-[148px]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          {shouldRenderVisibleHeader ? (
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : undefined}
              className="mb-12 text-center"
            >
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
                {normalizedTitle}
              </h1>
              {normalizedDescription.length > 0 && (
                <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                  {normalizedDescription}
                </p>
              )}
            </motion.header>
          ) : (
            normalizedTitle.length > 0 && !suppressFallbackHeading && (
              <>
                <h1 className="sr-only">{normalizedTitle}</h1>
                {normalizedDescription.length > 0 && (
                  <p className="sr-only">{normalizedDescription}</p>
                )}
              </>
            )
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={contentTransition}
          >
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  )
} 